/**
 * Participants are unauthenticated — their identity is stored in
 * localStorage keyed by event slug.
 */

const KEY_PREFIX = "finally.meetup.participant.";
const LAST_KEY = "finally.meetup.last-participant";

interface ParticipantSession {
  participantId: string;
  eventId: string;
  name: string;
  email: string;
}

interface LastParticipant {
  participantId: string;
  slug: string;
  name: string;
  email: string;
}

export function saveParticipantSession(slug: string, session: ParticipantSession) {
  localStorage.setItem(KEY_PREFIX + slug, JSON.stringify(session));
  saveLastParticipant({
    participantId: session.participantId,
    slug,
    name: session.name,
    email: session.email,
  });
}

export function getParticipantSession(slug: string): ParticipantSession | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + slug);
    if (!raw) return null;
    return JSON.parse(raw) as ParticipantSession;
  } catch {
    return null;
  }
}

export function clearParticipantSession(slug: string) {
  localStorage.removeItem(KEY_PREFIX + slug);
  // Reset the global "last participant" pointer if it pointed to this slug —
  // otherwise Account Home would still try to enumerate from a deleted seat.
  const last = getLastParticipant();
  if (last?.slug === slug) {
    localStorage.removeItem(LAST_KEY);
  }
}

/**
 * Save a global "last seen" participant pointer. Account Home (`/me`) uses
 * this when the URL doesn't carry a `?pid=`: we read whoever the device most
 * recently identified as and enumerate their events from there.
 */
export function saveLastParticipant(p: LastParticipant): void {
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify(p));
  } catch {
    /* storage full / disabled — non-critical */
  }
}

export function getLastParticipant(): LastParticipant | null {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LastParticipant;
  } catch {
    return null;
  }
}
